import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import EventIcon from '@mui/icons-material/Event';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { format } from 'date-fns';
import { sentenceToSnakeCase } from 'feature/loading-management/utils/format';
import { theme } from 'library';
import AuthSequencingItem from './AuthSequencingItem';

const sequenceHeaderStyle = () => ({
  alignItems: 'center',
  backgroundColor: theme.palette._next.common.black._states.hover,
  borderColor: theme.palette._next.table.tableHeader,
  borderLeft: '3px solid',
  display: 'flex',
  height: '40px',
  justifyContent: 'space-between',
  px: 2,
  py: 1,
});

const AuthorizationSequencingCardContent = forwardRef(
  (
    { authSequenceData, onAuthSequenceChange, onFutureAuthSequenceClick, onStatusChange, canEdit },
    ref
  ) => {
    const [sequenced, setSequenced] = useState(authSequenceData.sequenced || []);
    const [notSequenced, setNotSequenced] = useState(authSequenceData.notSequenced || []);
    const [changedAuthIds, setChangedAuthIds] = useState(new Set());
    const [originalState, setOriginalState] = useState(null);

    // Helper function to sort notSequenced array alphabetically by inventoryOwner
    const sortByInventoryOwner = useCallback(auths => {
      return [...auths].sort((a, b) => {
        const nameA = a.inventoryOwner?.toLowerCase() || '';
        const nameB = b.inventoryOwner?.toLowerCase() || '';
        return nameA.localeCompare(nameB);
      });
    }, []);

    // Helper function to generate auth ID
    const getAuthId = useCallback(auth => {
      return `${sentenceToSnakeCase(auth.inventoryOwner)}${
        auth.supplierDirectedBlend ? '-SDB' : ''
      }`;
    }, []);

    // Helper function to recalculate all changed auth IDs
    const recalculateChangedAuthIds = useCallback(
      (currentSequenced, currentNotSequenced) => {
        if (!originalState) return new Set();

        const newChangedIds = new Set();

        // Check all sequenced auths
        currentSequenced.forEach((auth, index) => {
          const authId = getAuthId(auth);
          const originalIndex = originalState.sequenced.findIndex(a => getAuthId(a) === authId);
          // If auth wasn't originally at this position in sequenced, mark as changed
          if (originalIndex !== index) {
            newChangedIds.add(authId);
          }
        });

        // Check all not sequenced auths
        currentNotSequenced.forEach(auth => {
          const authId = getAuthId(auth);
          const wasInOriginalSequenced = originalState.sequenced.some(a => getAuthId(a) === authId);
          // If auth was originally in sequenced but now in notSequenced, mark as changed
          if (wasInOriginalSequenced) {
            newChangedIds.add(authId);
          }
        });

        return newChangedIds;
      },
      [originalState, getAuthId]
    );

    useEffect(() => {
      const newSequenced = authSequenceData.sequenced || [];
      const newNotSequenced = sortByInventoryOwner(authSequenceData.notSequenced || []);
      setSequenced(newSequenced);
      setNotSequenced(newNotSequenced);
      // Store original state for comparison
      setOriginalState({
        notSequenced: newNotSequenced,
        sequenced: newSequenced,
      });
    }, [authSequenceData, sortByInventoryOwner]);

    // Expose reset method and getCurrentState to parent component
    useImperativeHandle(
      ref,
      () => ({
        applySavedState: savedState => {
          const nextSequenced = savedState?.sequenced || [];
          const nextNotSequenced = sortByInventoryOwner(savedState?.notSequenced || []);

          setOriginalState({
            notSequenced: nextNotSequenced,
            sequenced: nextSequenced,
          });
          setChangedAuthIds(new Set());
          setSequenced(nextSequenced);
          setNotSequenced(nextNotSequenced);
        },
        compareStateToOriginal: state => {
          if (!originalState) return false;

          const { sequenced: stateSequenced, notSequenced: stateNotSequenced } = state;

          // Check if sequenced lists are different (length or order)
          if (stateSequenced.length !== originalState.sequenced.length) return true;

          // Check if any items are in different positions or have moved between lists
          const hasSequenceChanges = stateSequenced.some((auth, index) => {
            const authId = getAuthId(auth);
            const originalAuth = originalState.sequenced[index];
            return !originalAuth || getAuthId(originalAuth) !== authId;
          });

          if (hasSequenceChanges) return true;

          // Check if notSequenced has different items (items moved to/from sequenced)
          const currentNotSequencedIds = new Set(stateNotSequenced.map(getAuthId));
          const originalNotSequencedIds = new Set(originalState.notSequenced.map(getAuthId));

          if (currentNotSequencedIds.size !== originalNotSequencedIds.size) return true;

          // Check if any IDs are different
          const hasDifferentIds = Array.from(currentNotSequencedIds).some(
            id => !originalNotSequencedIds.has(id)
          );

          return hasDifferentIds;
        },
        getCurrentState: () => ({
          notSequenced,
          sequenced,
        }),
        hasChanges: () => {
          if (!originalState) return false;

          // Check if sequenced lists are different (length or order)
          if (sequenced.length !== originalState.sequenced.length) return true;

          // Check if any items are in different positions or have moved between lists
          const hasSequenceChanges = sequenced.some((auth, index) => {
            const authId = getAuthId(auth);
            const originalAuth = originalState.sequenced[index];
            return !originalAuth || getAuthId(originalAuth) !== authId;
          });

          if (hasSequenceChanges) return true;

          // Check if notSequenced has different items (items moved to/from sequenced)
          const currentNotSequencedIds = new Set(notSequenced.map(getAuthId));
          const originalNotSequencedIds = new Set(originalState.notSequenced.map(getAuthId));

          if (currentNotSequencedIds.size !== originalNotSequencedIds.size) return true;

          // Check if any IDs are different
          const hasDifferentIds = Array.from(currentNotSequencedIds).some(
            id => !originalNotSequencedIds.has(id)
          );

          return hasDifferentIds;
        },
        hideSavingState: () => {
          if (onStatusChange) {
            onStatusChange(null);
          }
        },
        reset: () => {
          // Reset to the last saved state (originalState) if available,
          // otherwise fall back to initial authSequenceData
          if (originalState) {
            setSequenced(originalState.sequenced);
            setNotSequenced(originalState.notSequenced);
          } else {
            setSequenced(authSequenceData.sequenced || []);
            setNotSequenced(authSequenceData.notSequenced || []);
          }
          setChangedAuthIds(new Set());
        },
        showErrorState: () => {
          if (onStatusChange) {
            onStatusChange('error');
            setTimeout(() => {
              onStatusChange(null);
            }, 2000);
          }
        },
        showSavingState: () => {
          if (onStatusChange) {
            onStatusChange('progress');
          }
        },
        showSuccessState: () => {
          if (onStatusChange) {
            onStatusChange('success');
            setTimeout(() => {
              onStatusChange(null);
            }, 2000);
          }
        },
      }),
      [
        sortByInventoryOwner,
        notSequenced,
        sequenced,
        originalState,
        getAuthId,
        onStatusChange,
        authSequenceData,
      ]
    );

    const hasSequencedAuths = sequenced?.length > 0;
    const hasUnsequencedAuths = notSequenced?.length > 0;

    const isAuthChanged = useCallback(
      auth => {
        const authId = `${sentenceToSnakeCase(auth.inventoryOwner)}${
          auth.supplierDirectedBlend ? '-SDB' : ''
        }`;
        return changedAuthIds.has(authId);
      },
      [changedAuthIds]
    );

    const handleAddToSequenced = useCallback(
      index => {
        // Get the item from notSequenced
        const itemToAdd = notSequenced[index];

        // Remove from notSequenced
        const newNotSequenced = notSequenced.filter((_, idx) => idx !== index);

        // Add to bottom of sequenced with proper sequence number
        const newSequenced = [...sequenced, { ...itemToAdd, sequenceNumber: sequenced.length + 1 }];

        // Recalculate all changed auth IDs
        const sortedNotSequenced = sortByInventoryOwner(newNotSequenced);
        setChangedAuthIds(recalculateChangedAuthIds(newSequenced, sortedNotSequenced));

        // Update state
        setSequenced(newSequenced);
        setNotSequenced(sortedNotSequenced);

        // Notify parent of user-initiated change
        if (onAuthSequenceChange) {
          onAuthSequenceChange({
            notSequenced: sortedNotSequenced,
            sequenced: newSequenced,
          });
        }
      },
      [
        notSequenced,
        sequenced,
        onAuthSequenceChange,
        sortByInventoryOwner,
        recalculateChangedAuthIds,
      ]
    );

    const handleRemoveFromSequenced = useCallback(
      index => {
        // Get the item from sequenced
        const itemToRemove = sequenced[index];

        // Remove from sequenced
        const newSequenced = sequenced
          .filter((_, idx) => idx !== index)
          .map((auth, idx) => ({
            ...auth,
            sequenceNumber: idx + 1,
          }));

        // Add to top of notSequenced with no sequence number
        const newNotSequenced = [{ ...itemToRemove, sequenceNumber: null }, ...notSequenced];

        // Recalculate all changed auth IDs
        const sortedNotSequenced = sortByInventoryOwner(newNotSequenced);
        setChangedAuthIds(recalculateChangedAuthIds(newSequenced, sortedNotSequenced));

        // Update state
        setSequenced(newSequenced);
        setNotSequenced(sortedNotSequenced);

        // Notify parent of user-initiated change
        if (onAuthSequenceChange) {
          onAuthSequenceChange({
            notSequenced: sortedNotSequenced,
            sequenced: newSequenced,
          });
        }
      },
      [
        sequenced,
        notSequenced,
        onAuthSequenceChange,
        sortByInventoryOwner,
        recalculateChangedAuthIds,
      ]
    );

    const onDragStart = useCallback(() => {
      // Placeholder for future drag start logic if needed
    }, []);

    const onDragEnd = useCallback(
      result => {
        const { source, destination } = result;

        // Dropped outside a valid droppable
        if (!destination) {
          return;
        }

        // Dropped in the same position
        if (source.droppableId === destination.droppableId && source.index === destination.index) {
          return;
        }

        // Prevent reordering within notSequenced - only allow drag in/out
        if (source.droppableId === 'notSequenced' && destination.droppableId === 'notSequenced') {
          return;
        }

        let newSequenced = sequenced;
        let newNotSequenced = notSequenced;

        // Same list - reorder within the same droppable
        if (source.droppableId === destination.droppableId) {
          const list = source.droppableId === 'sequenced' ? [...sequenced] : [...notSequenced];
          const [movedItem] = list.splice(source.index, 1);
          list.splice(destination.index, 0, movedItem);

          if (source.droppableId === 'sequenced') {
            // Update sequence numbers
            newSequenced = list.map((auth, idx) => ({
              ...auth,
              sequenceNumber: idx + 1,
            }));
          } else {
            newNotSequenced = list;
          }
        } else {
          // Different lists - moving between sequenced and not sequenced
          const sourceList =
            source.droppableId === 'sequenced' ? [...sequenced] : [...notSequenced];
          const destList =
            destination.droppableId === 'sequenced' ? [...sequenced] : [...notSequenced];

          const [movedItem] = sourceList.splice(source.index, 1);

          // Update sequence number based on destination
          let updatedMovedItem = movedItem;
          if (destination.droppableId === 'sequenced') {
            // Moving to sequenced - assign sequence number
            updatedMovedItem = { ...movedItem, sequenceNumber: destination.index + 1 };
          } else {
            // Moving to not sequenced - remove sequence number
            updatedMovedItem = { ...movedItem, sequenceNumber: null };
          }

          destList.splice(destination.index, 0, updatedMovedItem);

          if (destination.droppableId === 'sequenced') {
            // Update sequence numbers for sequenced list
            newSequenced = destList.map((auth, idx) => ({
              ...auth,
              sequenceNumber: idx + 1,
            }));
            newNotSequenced = sourceList;
          } else {
            // Moving to not sequenced
            newSequenced = sourceList.map((auth, idx) => ({
              ...auth,
              sequenceNumber: idx + 1,
            }));
            newNotSequenced = destList;
          }
        }

        // Sort notSequenced alphabetically
        newNotSequenced = sortByInventoryOwner(newNotSequenced);

        // Recalculate all changed auth IDs
        setChangedAuthIds(recalculateChangedAuthIds(newSequenced, newNotSequenced));

        setSequenced(newSequenced);
        setNotSequenced(newNotSequenced);

        // Notify parent of user-initiated change
        if (onAuthSequenceChange) {
          onAuthSequenceChange({
            notSequenced: newNotSequenced,
            sequenced: newSequenced,
          });
        }
      },
      [
        sequenced,
        notSequenced,
        onAuthSequenceChange,
        sortByInventoryOwner,
        recalculateChangedAuthIds,
      ]
    );

    const futureSequenceDate =
      authSequenceData.futureSequenceDate ||
      [...sequenced, ...notSequenced].find(a => !!a?.futureSequenceDate)?.futureSequenceDate ||
      null;
    const hasFutureSequenceDate = Boolean(futureSequenceDate);

    return (
      <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
        <Box sx={sequenceHeaderStyle}>
          <Typography sx={{ mt: '3px' }} variant="subtitle2">
            SEQUENCED
          </Typography>
          {(canEdit || hasFutureSequenceDate) && (
            <Stack
              alignItems="center"
              direction="row"
              gap={1}
              sx={{
                color: hasFutureSequenceDate ? '_next.secondary.main' : '_next.primary.dark',
                ml: 2,
              }}
            >
              <Box
                component="button"
                onClick={onFutureAuthSequenceClick}
                sx={{
                  '&:hover .MuiChip-root': {
                    backgroundColor: theme.palette._next.secondary._states.hover,
                  },
                  '&:hover > .MuiSvgIcon-root': {
                    color: theme.palette._next.secondary.main,
                  },
                  alignItems: 'center',
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  cursor: 'pointer',
                  display: 'flex',
                  gap: 1,
                  height: '24px',
                  minWidth: '24px',
                  p: 0,
                }}
              >
                {canEdit && <EventIcon sx={{ height: '24px', width: '24px' }} />}
                {hasFutureSequenceDate && (
                  <Chip
                    label={format(new Date(futureSequenceDate), 'MM/dd/yyyy')}
                    sx={{
                      borderColor: 'currentColor',
                      color: 'inherit',
                      fontFamily: 'Overpass',
                      fontSize: '13px',
                      height: '24px',
                      pointerEvents: 'none',
                      pt: '2px',
                    }}
                    variant="outlined"
                  />
                )}
                <KeyboardArrowRightIcon sx={{ height: '24px', width: '24px' }} />
              </Box>
            </Stack>
          )}
        </Box>
        <Droppable droppableId="sequenced" isDropDisabled={!canEdit}>
          {(provided, _snapshot) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {hasSequencedAuths ? (
                sequenced?.map((auth, index) => (
                  <Draggable
                    draggableId={`sequenced-${sentenceToSnakeCase(auth.inventoryOwner)}${
                      auth.supplierDirectedBlend ? '-SDB' : ''
                    }`}
                    index={index}
                    isDragDisabled={!canEdit}
                    key={`sequenced-${sentenceToSnakeCase(auth.inventoryOwner)}${
                      auth.supplierDirectedBlend ? '-SDB' : ''
                    }`}
                  >
                    {(_provided, snapshot) => (
                      <div
                        ref={_provided.innerRef}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {..._provided.draggableProps}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {..._provided.dragHandleProps}
                      >
                        <AuthSequencingItem
                          canEdit={canEdit}
                          index={index}
                          isChanged={isAuthChanged(auth)}
                          isDragging={snapshot.isDragging}
                          onRemoveSeq={handleRemoveFromSequenced}
                          sequenced
                          {...auth}
                        />
                      </div>
                    )}
                  </Draggable>
                ))
              ) : (
                <Stack
                  alignItems="center"
                  direction="row"
                  gap={1}
                  sx={{ borderBottom: '1px solid', borderColor: 'divider', p: 0 }}
                >
                  {_snapshot.isDraggingOver ? (
                    <></>
                  ) : (
                    <Typography color="text.secondary" sx={{ p: 2 }} variant="tab">
                      None
                    </Typography>
                  )}
                </Stack>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
        <Box sx={sequenceHeaderStyle}>
          <Typography sx={{ mt: '3px' }} variant="subtitle2">
            NOT SEQUENCED
          </Typography>
        </Box>
        <Droppable droppableId="notSequenced" isDropDisabled={!canEdit}>
          {(provided, _snapshot) => (
            // eslint-disable-next-line react/jsx-props-no-spreading
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {hasUnsequencedAuths ? (
                notSequenced.map((auth, index) => (
                  <Draggable
                    draggableId={`notSequenced-${sentenceToSnakeCase(auth.inventoryOwner)}${
                      auth.supplierDirectedBlend ? '-SDB' : ''
                    }`}
                    index={index}
                    isDragDisabled={!canEdit}
                    key={`notSequenced-${sentenceToSnakeCase(auth.inventoryOwner)}${
                      auth.supplierDirectedBlend ? '-SDB' : ''
                    }`}
                  >
                    {(_provided, snapshot) => (
                      <div
                        ref={_provided.innerRef}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {..._provided.draggableProps}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {..._provided.dragHandleProps}
                      >
                        <AuthSequencingItem
                          canEdit={canEdit}
                          index={index}
                          isChanged={isAuthChanged(auth)}
                          isDragging={snapshot.isDragging}
                          {...auth}
                          onAddSeq={handleAddToSequenced}
                          sequenced={false}
                        />
                      </div>
                    )}
                  </Draggable>
                ))
              ) : (
                <Stack
                  alignItems="center"
                  direction="row"
                  gap={1}
                  sx={{ borderBottom: '1px solid', borderColor: 'divider', p: 0 }}
                >
                  {_snapshot.isDraggingOver ? (
                    <></>
                  ) : (
                    <Typography color="text.secondary" sx={{ p: 2 }} variant="tab">
                      None
                    </Typography>
                  )}
                </Stack>
              )}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  }
);

const authSequencesShape = {
  inventoryOwner: PropTypes.string,
  isActive: PropTypes.bool,
  productAuthorizationId: PropTypes.number,
  sequenceNumber: PropTypes.number,
  supplierDirectedBlend: PropTypes.bool,
};

AuthorizationSequencingCardContent.propTypes = {
  authSequenceData: PropTypes.shape({
    futureSequenceDate: PropTypes.string,
    notSequenced: PropTypes.arrayOf(PropTypes.shape(authSequencesShape)).isRequired,
    sequenced: PropTypes.arrayOf(PropTypes.shape(authSequencesShape)).isRequired,
  }).isRequired,
  canEdit: PropTypes.bool,
  onAuthSequenceChange: PropTypes.func,
  onFutureAuthSequenceClick: PropTypes.func,
  onStatusChange: PropTypes.func,
};

AuthorizationSequencingCardContent.defaultProps = {
  canEdit: false,
  onAuthSequenceChange: null,
  onFutureAuthSequenceClick: null,
  onStatusChange: null,
};

export default AuthorizationSequencingCardContent;
